import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/shell/Header'
import { listClients, listAssignments, listEmployees } from '../lib/api'
import { ClientStatRow, ClientCard, ClientBoard, ClientIntake } from '../components/client'
import { ClientOnboardImport } from '../components/client/onboard/ClientOnboardImport'
import { useSession } from '../hooks/useAuth'
import { roleCanSeeFinancials, roleCanManage } from '@shared/enums'
import {
  Plus,
  Search,
  LayoutGrid,
  Columns3,
  List,
  CheckCircle2,
  Sheet,
} from 'lucide-react'

type ViewMode = 'grid' | 'board' | 'list'
type GroupBy = 'health' | 'vertical' | 'plan' | 'account_manager'

const HEALTH_OPTIONS = [
  { key: '', label: 'All', dot: '' },
  { key: 'good', label: 'Healthy', dot: 'var(--state-success)' },
  { key: 'watch', label: 'Watch', dot: 'var(--state-warning)' },
  { key: 'at-risk', label: 'At risk', dot: 'var(--state-danger)' },
] as const

const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: 'health', label: 'Health' },
  { key: 'account_manager', label: 'Account manager' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'plan', label: 'Plan' },
]

export function Clients() {
  const { data: session } = useSession()
  const role = (session?.user as { role?: string } | undefined)?.role ?? 'member'
  const canSeeFinancials = roleCanSeeFinancials(role)
  // Adding and importing clients is admin work; the Team role browses (LXP-55).
  const canManage = roleCanManage(role)
  const [search, setSearch] = useState('')
  const [healthFilter, setHealthFilter] = useState('')
  const [view, setView] = useState<ViewMode>('grid')
  const [groupBy, setGroupBy] = useState<GroupBy>('health')
  const [showAdd, setShowAdd] = useState(false)
  const [showOnboard, setShowOnboard] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const params: Record<string, string> = {}
  if (search) params.search = search
  if (healthFilter) params.health = healthFilter

  const { data, isLoading } = useQuery({
    queryKey: ['clients', params],
    queryFn: () => listClients(params),
  })

  // Account-manager grouping needs the client assignments + roster.
  const { data: clientAssignmentsData } = useQuery({
    queryKey: ['assignments', { entityType: 'client' }],
    queryFn: () => listAssignments({ entityType: 'client' }),
    enabled: view === 'board' && groupBy === 'account_manager',
  })
  const { data: employeesData } = useQuery({
    queryKey: ['employees'],
    queryFn: () => listEmployees(),
    enabled: view === 'board' && groupBy === 'account_manager',
  })

  // clientId → account-manager display name.
  const amByClient = new Map<string, string>()
  {
    const empName = new Map((employeesData?.items ?? []).map((e) => [e.id, e.name]))
    for (const a of clientAssignmentsData?.items ?? []) {
      if (a.role === 'account_manager') {
        amByClient.set(a.entityId, empName.get(a.employeeId) ?? 'Unknown')
      }
    }
  }

  // Auto-dismiss the toast.
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3200)
    return () => clearTimeout(t)
  }, [toast])

  const clients = (data?.items ?? []).sort((a, b) => {
    // Internal clients are pinned to the top (LXP-47)
    if (a.kind === 'internal' && b.kind !== 'internal') return -1
    if (a.kind !== 'internal' && b.kind === 'internal') return 1
    // Then sort alphabetically by name
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="page">
      <Header title="Clients" />

      {/* Stat row */}
      <ClientStatRow clients={clients} showFinancials={canSeeFinancials} />

      {/* Toolbar */}
      <div className="clients-toolbar">
        {/* Search */}
        <div className="search-input">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search clients…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Health filter */}
        <div className="filter-group">
          {HEALTH_OPTIONS.map((h) => (
            <button
              key={h.key}
              className={`filter-btn${healthFilter === h.key ? ' filter-btn--active' : ''}`}
              onClick={() => setHealthFilter(h.key)}
            >
              {h.dot && (
                <span
                  className="health-dot"
                  style={{ background: h.dot }}
                />
              )}
              {h.label}
            </button>
          ))}
        </div>

        {/* Group by (shown when board view is active) */}
        {view === 'board' && (
          <div className="filter-group clients-toolbar__group">
            <span className="clients-toolbar__label">Group</span>
            {/* Account-manager grouping reads client staffing, which only admin
                and up can list — for the Team role it would be one empty column. */}
            {GROUP_OPTIONS.filter((g) => canManage || g.key !== 'account_manager').map((g) => (
              <button
                key={g.key}
                className={`filter-btn${groupBy === g.key ? ' filter-btn--active' : ''}`}
                onClick={() => setGroupBy(g.key)}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}

        {/* Right side: view toggle + add */}
        <div className="clients-toolbar__right">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn${view === 'grid' ? ' view-toggle-btn--active' : ''}`}
              onClick={() => setView('grid')}
              aria-label="Grid view"
              title="Grid view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`view-toggle-btn${view === 'board' ? ' view-toggle-btn--active' : ''}`}
              onClick={() => setView('board')}
              aria-label="Board view"
              title="Board view"
            >
              <Columns3 size={16} />
            </button>
            <button
              className={`view-toggle-btn${view === 'list' ? ' view-toggle-btn--active' : ''}`}
              onClick={() => setView('list')}
              aria-label="List view"
              title="List view"
            >
              <List size={16} />
            </button>
          </div>
          {canManage && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowOnboard(true)} title="Onboard clients + team + tasks from a spreadsheet">
                <Sheet size={16} /> Onboard
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
                <Plus size={16} /> Add client
              </button>
            </>
          )}
        </div>
      </div>

      {/* Onboarding importer */}
      {canManage && showOnboard && (
        <ClientOnboardImport onClose={() => setShowOnboard(false)} onSuccess={(msg) => setToast(msg)} />
      )}

      {/* Intake modal */}
      {canManage && showAdd && (
        <ClientIntake
          onClose={() => setShowAdd(false)}
          onSuccess={(msg) => setToast(msg)}
          onSwitchToOnboard={() => {
            setShowAdd(false)
            setShowOnboard(true)
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="intake-toast" role="status">
          <CheckCircle2 size={16} />
          <span>{toast}</span>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="clients-loading">
          <div className="loading-spinner" />
        </div>
      ) : clients.length === 0 ? (
        <div className="clients-empty">
          <p>No clients match your filters.</p>
          {canManage && !search && !healthFilter && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
              <Plus size={16} /> Add your first client
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Board view */}
          {view === 'board' && <ClientBoard clients={clients} groupBy={groupBy} amByClient={amByClient} />}

          {/* Grid view */}
          {view === 'grid' && (
            <div className="client-grid-v2">
              {clients.map((c) => (
                <ClientCard key={c.id} client={c} variant="grid" />
              ))}
            </div>
          )}

          {/* List view */}
          {view === 'list' && (
            <div className="client-list-v2">
              <div className="client-list-v2__header">
                <span>Client</span>
                <span>Plan</span>
                {canSeeFinancials && <span>MRR</span>}
                <span>Health</span>
              </div>
              {clients.map((c) => (
                <ClientCard key={c.id} client={c} variant="row" showFinancials={canSeeFinancials} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
